export interface DoorDesign {
  id: string;
  name: string;
  collection: 'traditional' | 'modern' | 'cottage' | 'deluxe';
  panelType: string;
  tagline: string;
  description: string;
  popular?: boolean;
  newDesign?: boolean;
}

export interface DoorColour {
  name: string;
  hex: string;
  isLight: boolean;
  category: 'standard' | 'woodgrain' | 'deluxe-matte';
}

export interface GlassStyle {
  id: string;
  name: string;
  desc: string;
  category: 'modern' | 'traditional' | 'privacy' | 'solid';
}

export const doorColours: DoorColour[] = [
  { name: 'White', hex: '#FAFAF8', isLight: true, category: 'standard' },
  { name: 'Cream', hex: '#F5F0E8', isLight: true, category: 'standard' },
  { name: 'Anthracite Grey', hex: '#374649', isLight: false, category: 'standard' },
  { name: 'Black', hex: '#1A1A1A', isLight: false, category: 'standard' },
  { name: 'Chartwell Green', hex: '#6B7C5C', isLight: false, category: 'standard' },
  { name: 'Rosewood', hex: '#7B3B3B', isLight: false, category: 'woodgrain' },
  { name: 'Golden Oak', hex: '#9B7A4A', isLight: false, category: 'woodgrain' },
  { name: 'Irish Oak', hex: '#8B6B45', isLight: false, category: 'woodgrain' },
  { name: 'Duck Egg Blue', hex: '#7EADB5', isLight: false, category: 'standard' },
  { name: 'Agate Grey', hex: '#8E9196', isLight: false, category: 'deluxe-matte' },
  { name: 'Silent Midnight', hex: '#1E2030', isLight: false, category: 'deluxe-matte' },
  { name: 'Deep Lagoon', hex: '#1B4D5C', isLight: false, category: 'deluxe-matte' },
  { name: 'Regal Green', hex: '#2E4A3A', isLight: false, category: 'deluxe-matte' },
  { name: 'Pottery Red', hex: '#8B3A2A', isLight: false, category: 'deluxe-matte' },
];

export const glassStyles: GlassStyle[] = [
  { id: 'solid', name: 'Solid', desc: 'No glazing — maximum privacy & insulation', category: 'solid' },
  { id: 'ice-edge', name: 'Ice Edge', desc: 'Frosted finish with a decorative clear border — sleek and contemporary', category: 'modern' },
  { id: 'pinstripe', name: 'Pinstripe', desc: 'Clean linear sandblasted lines for a minimal, architectural look', category: 'modern' },
  { id: 'aurora', name: 'Aurora', desc: 'Flowing sandblasted curves — modern and softly decorative', category: 'modern' },
  { id: 'classic-diamond', name: 'Classic Diamond', desc: 'Traditional diamond lead caming — the timeless choice for classic doors', category: 'traditional' },
  { id: 'climbing-rose', name: 'Climbing Rose', desc: 'Intricate traditional pewter lead rose design — charming and elegant', category: 'traditional' },
  { id: 'danthorpe-cross', name: 'Danthorpe Cross', desc: 'Polished zinc cameing with crystal bevel glass and Pelerine backing', category: 'traditional' },
  { id: 'blue-diamond', name: 'Blue Diamond', desc: 'Coloured diamond lead — also available in Red, Black & Green', category: 'traditional' },
  { id: 'murano', name: 'Murano', desc: 'Vibrant fused glass tiles — available in Red, Blue, Black & Green', category: 'traditional' },
  { id: 'frosted', name: 'Cotswold', desc: 'Pilkington Cotswold privacy glass — Level 5 obscure for maximum privacy', category: 'privacy' },
];

export const doorDesigns: DoorDesign[] = [
  {
    id: 'tuscan-3',
    name: 'Tuscan 3',
    collection: 'traditional',
    panelType: 'four-panel',
    tagline: 'Classic Georgian four-panel — top seller',
    description: 'The Tuscan 3 is Hallmark\'s best-selling composite door. Four perfectly proportioned raised panels in the classic Georgian arrangement make it a universally loved choice for both period and modern homes. Fitted with multi-point locking as standard.',
    popular: true,
  },
  {
    id: 'tuscan-1',
    name: 'Tuscan 1',
    collection: 'traditional',
    panelType: 'single-panel',
    tagline: 'Bold single raised panel classic',
    description: 'The Tuscan 1 distils the traditional composite door to a single commanding raised panel. Clean, strong, and deceptively simple — it works beautifully with period properties and suits homes that want classic kerb appeal without fussiness.',
  },
  {
    id: 'camarque-4',
    name: 'Camarque 4',
    collection: 'traditional',
    panelType: 'four-panel',
    tagline: 'Four-panel with elegant proportions',
    description: 'The Camarque 4 features four generously sized raised panels with refined shadow lines. A slightly wider panel profile than the Tuscan gives it a more formal character — well-suited to larger detached homes and wider door openings.',
  },
  {
    id: 'camarque-2',
    name: 'Camarque 2',
    collection: 'traditional',
    panelType: 'two-panel',
    tagline: 'Two bold raised panels, simply stated',
    description: 'Two large vertically stacked raised panels give the Camarque 2 a confident, uncluttered look. An ideal choice when you want a traditional door with a slightly modern sensibility — less busy than four panels but undeniably classic.',
  },
  {
    id: 'arnage-2',
    name: 'Arnage 2',
    collection: 'traditional',
    panelType: 'two-panel-top-glass',
    tagline: 'Glazed top with two lower panels',
    description: 'The Arnage 2 pairs a decorative glazed section with two solid raised lower panels. The glazing brings natural light into your hallway while maintaining a traditional aesthetic. A favourite for front entrances on semi-detached and Victorian terraced homes.',
  },
  {
    id: 'esprit-2',
    name: 'Esprit 2',
    collection: 'traditional',
    panelType: 'two-panel-top-glass',
    tagline: 'Traditional glazed entrance door',
    description: 'The Esprit 2 features a wide decorative glass panel above two solid lower panels. The Esprit\'s taller glazing section floods your hallway with light and pairs beautifully with both traditional lead glass and modern sandblasted designs.',
  },
  {
    id: 'mulsanne-1',
    name: 'Mulsanne 1',
    collection: 'traditional',
    panelType: 'single-panel-glass',
    tagline: 'Single panel with bold glazed section',
    description: 'A generous glazed upper section sits above a single large raised panel on the Mulsanne 1. Classic, welcoming, and wonderfully versatile — it works equally well with traditional decorative lead glass and contemporary sandblasted designs.',
  },
  {
    id: 'hagley-2',
    name: 'Hagley 2',
    collection: 'traditional',
    panelType: 'two-panel',
    tagline: 'Refined stacked panel traditional',
    description: 'The Hagley 2 features two broad raised panels with a slightly deeper shadow line detail than the Camarque range. A solid, unpretentious traditional door that sits beautifully on a wide range of UK home styles from Victorian terraces to 1930s semis.',
  },
  {
    id: 'aruba-4',
    name: 'Aruba 4',
    collection: 'modern',
    panelType: 'four-panel',
    tagline: 'Modern four-panel urban design',
    description: 'The Aruba 4 takes the classic four-panel arrangement and refines it for contemporary homes. Shallower shadow lines and a tighter panel proportion give it an urban edge while retaining a satisfying sense of structure. Pairs beautifully with bar handles.',
    popular: true,
  },
  {
    id: 'aruba-1',
    name: 'Aruba 1',
    collection: 'modern',
    panelType: 'single-panel',
    tagline: 'Contemporary single-panel statement',
    description: 'The Aruba 1 is a single large raised panel with modern proportions — clean, confident, and architectural. Works brilliantly on new-build homes, extensions, and any property with a contemporary aesthetic. Ideal paired with stainless bar hardware.',
  },
  {
    id: 'chimaera-1',
    name: 'Chimaera 1',
    collection: 'modern',
    panelType: 'flat',
    tagline: 'Flush smooth contemporary face',
    description: 'The Chimaera 1 is a completely flush, smooth-faced composite door — no panels, no detail. Pure, minimal, and architectural. Pairs with the full colour range and any hardware choice. A top pick for modern new-builds and Scandinavian-inspired homes.',
  },
  {
    id: 'cambridge',
    name: 'Cambridge',
    collection: 'modern',
    panelType: 'horizontal-bars',
    tagline: 'Bold horizontal groove lines',
    description: 'Three deep horizontal routed grooves divide the Cambridge into clean architectural sections. Confident, graphic, and unmistakably modern — it works brilliantly in Anthracite Grey or Agate Grey for a true statement entrance. Stainless bar handle recommended.',
    newDesign: true,
  },
  {
    id: 'seville',
    name: 'Seville',
    collection: 'modern',
    panelType: 'vertical-glass-strip',
    tagline: 'Vertical slim-line glass inset',
    description: 'A tall narrow glass strip sits alongside a smooth flush panel on the Seville. The asymmetric layout adds geometric interest and admits a slim column of natural light. Contemporary, distinctive, and a popular choice alongside aluminium windows.',
  },
  {
    id: 'debonaire',
    name: 'Debonaire',
    collection: 'modern',
    panelType: 'flat',
    tagline: 'Smooth flush with subtle texture',
    description: 'The Debonaire is a flush composite door with a subtle surface texture that catches light differently to a fully smooth door. The result is a sophisticated, understated entrance that works beautifully on everything from new-builds to modernised period homes.',
    newDesign: true,
  },
  {
    id: 'firenza',
    name: 'Firenza',
    collection: 'modern',
    panelType: 'porthole',
    tagline: 'Flush with circular porthole glazing',
    description: 'A smooth flush door face punctuated by a single circular porthole glass panel — bold, original, and surprisingly timeless. The Firenza is a genuine design statement for homeowners who want something genuinely different. Available across the full colour range.',
  },
  {
    id: 'hansa',
    name: 'Hansa',
    collection: 'modern',
    panelType: 'flat',
    tagline: 'Minimal urban flush composite',
    description: 'The Hansa features a completely flat GRP face with a fine wood-grain texture underlay visible at close range. It has the confidence of total minimalism — nothing to distract from a great colour choice or a stunning bar handle. A designer\'s favourite.',
  },
  {
    id: 'lancia-4',
    name: 'Lancia 4',
    collection: 'cottage',
    panelType: 'ledge-braced',
    tagline: 'Grooved cottage board effect',
    description: 'The Lancia 4 uses a series of vertically grooved boards to replicate the look of a traditional ledged country door with beautiful authenticity. The GRP skin captures every grain detail. A hugely popular choice for rural homes, cottages, and garden-facing rear doors.',
    popular: true,
  },
  {
    id: 'cortina-4',
    name: 'Cortina 4',
    collection: 'cottage',
    panelType: 'ledge-braced',
    tagline: 'Traditional barn board style',
    description: 'The Cortina 4 shares the Lancia\'s grooved cottage board aesthetic but with a slightly wider board profile and deeper groove shadow lines. More rustic in character — well suited to country homes, period cottages, and rear access doors on characterful properties.',
  },
  {
    id: 'cosworth-2',
    name: 'Cosworth 2',
    collection: 'cottage',
    panelType: 'cottage-glass',
    tagline: 'Half-glazed cottage entrance',
    description: 'A large decorative glass section sits above two solid lower panels on the Cosworth 2, creating a warm, welcoming cottage entrance. The generous glazing floods your hallway with natural light while the solid lower panels maintain privacy. A perennial best-seller for cottages.',
  },
  {
    id: 'lakewood-1',
    name: 'Lakewood 1',
    collection: 'cottage',
    panelType: 'cottage-glass',
    tagline: 'Open cottage with full glazed top',
    description: 'The Lakewood 1 features an even more generous glazed section than the Cosworth, with a single solid lower panel. It is the most light-admitting door in the cottage range and pairs particularly well with traditional lead glass designs. Beautiful for south-facing entrances.',
  },
  {
    id: 'sorpasso-1',
    name: 'Sorpasso 1',
    collection: 'cottage',
    panelType: 'single-panel',
    tagline: 'Country single panel door',
    description: 'The Sorpasso 1 takes the cottage collection in a cleaner direction — one large raised panel with a thicker, more robust border that evokes hand-crafted joinery. Versatile enough for both rural properties and suburban homes wanting a countryside-inspired character.',
  },
  {
    id: 'alpine-3',
    name: 'Alpine 3',
    collection: 'cottage',
    panelType: 'three-panel',
    tagline: 'Three panel country character',
    description: 'Three vertically arranged raised panels give the Alpine 3 a charming, honest character inspired by alpine and country architecture. The wider, shorter panel proportions give it a distinctly rural feel that looks stunning in Golden Oak, Irish Oak, or Rosewood.',
  },
  {
    id: 'escala-1',
    name: 'Escala 1',
    collection: 'deluxe',
    panelType: 'flat',
    tagline: 'Signature Deluxe premium flush',
    description: 'The Escala 1 is part of the Hallmark Signature Deluxe range — the very pinnacle of composite door performance. A premium-grade GRP skin with an ultra-fine texture, fitted with the HallMAX PRO 70mm double-rebate frame for superior weather sealing. Available exclusively with the Deluxe Matte colour range.',
    newDesign: true,
  },
  {
    id: 'impala-solid',
    name: 'Impala Solid',
    collection: 'deluxe',
    panelType: 'flat',
    tagline: 'Premium smooth Deluxe entrance',
    description: 'The Impala Solid is the smooth-faced flagship of the Deluxe range. Its completely flat GRP face is finished to an exceptionally tight tolerance for a door that looks as good after 15 years as on the day of installation. Paired with premium lever hardware as standard.',
    popular: true,
  },
  {
    id: 'mustang-3',
    name: 'Mustang 3',
    collection: 'deluxe',
    panelType: 'three-panel',
    tagline: 'Signature Deluxe three-panel',
    description: 'Three bold raised panels with deep architectural shadow lines distinguish the Mustang 3 as a premium door from first glance. Part of the Hallmark Signature Deluxe range, it is manufactured to tighter tolerances and fitted with the HallMAX PRO frame as standard.',
    newDesign: true,
  },
  {
    id: 'sorpasso-3',
    name: 'Sorpasso 3',
    collection: 'deluxe',
    panelType: 'three-panel',
    tagline: 'Deluxe three-panel statement door',
    description: 'The Sorpasso 3 elevates the three-panel format to Signature Deluxe specification. Each panel has a pronounced raised detail and a carefully engineered edge bevel that catches light beautifully. Available across the full Deluxe Matte colour range including Deep Lagoon and Silent Midnight.',
  },
];
